import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import CategoryIcon from "@/components/CategoryIcon";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import API from "@/lib/api";
import { geocode } from "@/lib/geo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Search, Check } from "lucide-react";
import { toast } from "sonner";

const RADII = [15, 30, 50, 100, "all"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { categories, catName } = useApp();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [start, setStart] = useState(null);
  const [radius, setRadius] = useState(30);
  const [online, setOnline] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const [interests, setInterests] = useState([]);

  const doSearch = async () => setResults(await geocode(query));
  const pick = (r) => { setStart({ city: r.city, lat: r.lat, lng: r.lng }); setQuery(r.city); setResults([]); };
  const toggle = (slug) => setInterests((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));

  const goStep2 = async () => {
    setSaving(true);
    try {
      await API.put("/me/zone", { country: "IT", start_point: start || {}, general_radius: radius, offer_online: online, per_category_radius: {} });
      setStep(2);
    } catch { toast.error("Errore nel salvataggio della zona"); }
    finally { setSaving(false); }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await API.put("/me/interests", { interests });
      await refresh();
      toast.success("Tutto pronto! Benvenuto su FYDATE");
      navigate("/");
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const skip = async () => {
    try { await API.put("/me/interests", { interests }); await refresh(); } catch {}
    navigate("/");
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto fade-in" data-testid="onboarding">
        <div className="flex items-center gap-2 mb-5">
          {[1, 2].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? "bg-[#1F4FE0]" : "bg-[#DADFEA]"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 space-y-5" data-testid="onboarding-step-1">
            <div>
              <h1 className="font-serif text-2xl font-bold text-[#0F1230]">Dove ti trovi?</h1>
              <p className="text-sm text-[#4A4E68] mt-1">Scegli la tua zona: vedrai richieste e attività vicino a te.</p>
            </div>
            <div>
              <p className="text-sm font-medium text-[#0F1230] mb-1">Città o CAP</p>
              <form onSubmit={(e) => { e.preventDefault(); doSearch(); }} className="flex gap-2">
                <Input data-testid="ob-city-input" placeholder="es. la tua città" value={query} onChange={(e) => setQuery(e.target.value)} />
                <Button type="submit" variant="outline" className="rounded-full shrink-0"><Search size={16} /></Button>
              </form>
              {results.length > 0 && (
                <div className="mt-2 border border-[#DADFEA] rounded-xl overflow-hidden">
                  {results.map((r, i) => (
                    <button key={i} data-testid={`ob-result-${i}`} onClick={() => pick(r)} className="block w-full text-left px-3 py-2 text-sm hover:bg-[#EEF1F7] border-b border-[#DADFEA] last:border-0">{r.label}</button>
                  ))}
                </div>
              )}
              {start?.city && <p className="text-xs text-[#2E7D32] mt-1" data-testid="ob-selected-city">{start.city}</p>}
            </div>
            <div>
              <p className="text-sm font-medium text-[#0F1230] mb-2">Raggio</p>
              <div className="flex flex-wrap gap-2">
                {RADII.map((r) => (
                  <button key={r} data-testid={`ob-radius-${r}`} onClick={() => setRadius(r)} className={`px-3 h-10 rounded-full text-sm border ${radius === r ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>
                    {r === "all" ? "Tutta Italia" : `${r} km`}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center justify-between">
              <span className="text-sm text-[#2A2E48]">Mi interessano anche servizi online</span>
              <Switch data-testid="ob-online" checked={online} onCheckedChange={setOnline} />
            </label>
            <Button data-testid="ob-next" onClick={goStep2} disabled={saving || (!start && radius !== "all")} className="w-full h-12 bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] rounded-full">Continua</Button>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 space-y-5" data-testid="onboarding-step-2">
            <div>
              <h1 className="font-serif text-2xl font-bold text-[#0F1230]">Cosa ti interessa?</h1>
              <p className="text-sm text-[#4A4E68] mt-1">Scegli le aree che segui. Potrai cambiarle quando vuoi.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((c) => {
                const on = interests.includes(c.slug);
                return (
                  <button key={c.slug} data-testid={`ob-interest-${c.slug}`} onClick={() => toggle(c.slug)}
                    className={`relative text-left rounded-xl border p-3 flex items-center gap-2 transition-colors ${on ? "border-[#1F4FE0] bg-[#FFF6DA]" : "border-[#DADFEA] bg-white"}`}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: c.tint.iconBg, color: c.tint.text }}>
                      <CategoryIcon name={c.icon} size={18} />
                    </div>
                    <span className="text-sm font-medium text-[#0F1230] leading-tight">{catName(c)}</span>
                    {on && <Check size={16} className="text-[#141A3A] absolute top-2 right-2" />}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button data-testid="ob-skip" variant="outline" onClick={skip} className="flex-1 h-12 rounded-full">Salta</Button>
              <Button data-testid="ob-finish" onClick={finish} disabled={saving} className="flex-1 h-12 bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] rounded-full">Inizia</Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
